import { useCallback, useEffect, useState } from "react";
import {
  getAdminConfig,
  getAdminProgress,
  logout,
  notifyExaminee,
  remindPreview,
  remindSend,
  updateAdminConfig,
} from "../lib/api";
import type { AdminConfig, AdminProgressResponse, EvaluatorProgress, RemindPreviewResponse } from "../lib/types";
import { useToast } from "../components/Toast";

const CONFIG_FIELDS: { key: keyof AdminConfig; label: string; placeholder?: string }[] = [
  { key: "activeMonth", label: "対象月(表示用ラベル)", placeholder: "2026-04" },
  { key: "sheetName", label: "月別タブ名", placeholder: "4月" },
  { key: "driveRootId", label: "Drive大元フォルダID" },
  { key: "deadline", label: "締切表示", placeholder: "2026/04/30" },
  { key: "answerCsvName", label: "評価対象答案CSV名", placeholder: "202604評価対象答案.csv" },
  { key: "reminderChannelId", label: "リマインド送信先チャンネルID" },
];

type Props = {
  onLogout: () => void;
  onSwitchToEvaluator?: () => void;
};

export default function AdminPortal({ onLogout, onSwitchToEvaluator }: Props) {
  const { showToast } = useToast();

  const [progress, setProgress] = useState<AdminProgressResponse | null>(null);
  const [progressLoading, setProgressLoading] = useState(true);
  const [progressError, setProgressError] = useState<string | null>(null);

  const [preview, setPreview] = useState<RemindPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const [notifyExaminerId, setNotifyExaminerId] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [notifySending, setNotifySending] = useState(false);
  const [notifyError, setNotifyError] = useState<string | null>(null);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [config, setConfig] = useState<AdminConfig | null>(null);
  const [configDraft, setConfigDraft] = useState<AdminConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSaving, setConfigSaving] = useState(false);

  const loadProgress = useCallback(async () => {
    setProgressLoading(true);
    setProgressError(null);
    const res = await getAdminProgress();
    setProgressLoading(false);
    if (res.ok) {
      setProgress(res.data);
    } else {
      setProgressError(res.error);
    }
  }, []);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    setConfigError(null);
    const res = await getAdminConfig();
    setConfigLoading(false);
    if (res.ok) {
      setConfig(res.data);
      setConfigDraft(res.data);
    } else {
      setConfigError(res.error);
    }
  }, []);

  function toggleSettings() {
    const next = !settingsOpen;
    setSettingsOpen(next);
    if (next && !config) {
      loadConfig();
    }
  }

  async function handleConfigSave() {
    if (!configDraft) return;
    setConfigSaving(true);
    setConfigError(null);
    const res = await updateAdminConfig(configDraft);
    setConfigSaving(false);
    if (!res.ok) {
      setConfigError(res.error);
      return;
    }
    showToast("設定を保存しました", "success");
    setConfig(configDraft);
    await loadProgress();
  }

  async function handlePreview() {
    setPreviewLoading(true);
    setPreviewError(null);
    setSendError(null);
    const res = await remindPreview();
    setPreviewLoading(false);
    if (res.ok) {
      setPreview(res.data);
    } else {
      setPreview(null);
      setPreviewError(res.error);
    }
  }

  async function handleSend() {
    if (!preview || preview.mentionedEvaluators.length === 0) return;
    const confirmed = window.confirm("Slackへ送信します。よろしいですか？");
    if (!confirmed) return;
    setSending(true);
    setSendError(null);
    const res = await remindSend();
    setSending(false);
    if (!res.ok) {
      setSendError(res.error);
      return;
    }
    showToast(`Slackへ送信しました（${res.data.mentionedCount}名）`, "success");
    setPreview(null);
  }

  async function handleNotifyExaminee() {
    if (!notifyExaminerId.trim() || !notifyMessage.trim()) {
      setNotifyError("添削者IDとメッセージ本文を入力してください");
      return;
    }
    const confirmed = window.confirm(
      `ID: ${notifyExaminerId.trim()} へ以下の内容でSlack送信します。よろしいですか？\n\n${notifyMessage}`
    );
    if (!confirmed) return;
    setNotifySending(true);
    setNotifyError(null);
    const res = await notifyExaminee(notifyExaminerId.trim(), notifyMessage.trim());
    setNotifySending(false);
    if (!res.ok) {
      setNotifyError(res.error);
      return;
    }
    showToast(`${res.data.name}さんへ送信しました`, "success");
    setNotifyExaminerId("");
    setNotifyMessage("");
  }

  const sortedByEvaluator: EvaluatorProgress[] = progress
    ? [...progress.byEvaluator].sort((a, b) => a.rate - b.rate)
    : [];

  async function handleLogout() {
    await logout();
    onLogout();
  }

  return (
    <div className="admin-portal">
      <header className="portal-header">
        <span className="portal-title">管理者ポータル</span>
        <div className="portal-header__actions">
          {onSwitchToEvaluator && (
            <button type="button" className="btn btn--ghost" onClick={onSwitchToEvaluator}>
              評価者ポータルへ切替
            </button>
          )}
          <button type="button" className="btn btn--ghost" onClick={toggleSettings}>
            {settingsOpen ? "設定を閉じる" : "設定"}
          </button>
          <button type="button" className="btn btn--ghost" onClick={loadProgress} disabled={progressLoading}>
            {progressLoading ? "更新中..." : "更新"}
          </button>
          <button type="button" className="btn btn--ghost" onClick={handleLogout}>
            ログアウト
          </button>
        </div>
      </header>

      {settingsOpen && (
        <div className="admin-settings">
          <h2 className="section-heading">設定(configシート)</h2>
          {configLoading && <p className="hint-text">読み込み中...</p>}
          {configError && <p className="error-text">{configError}</p>}
          {configDraft && (
            <>
              <div className="admin-settings__grid">
                <label className="login-field">
                  <span>評価受付ステータス</span>
                  <select
                    value={configDraft.status}
                    onChange={(e) =>
                      setConfigDraft({ ...configDraft, status: e.target.value as "open" | "closed" })
                    }
                  >
                    <option value="open">open(受付中)</option>
                    <option value="closed">closed(受付終了)</option>
                  </select>
                </label>
                {CONFIG_FIELDS.map((field) => (
                  <label className="login-field" key={field.key}>
                    <span>{field.label}</span>
                    <input
                      type="text"
                      value={configDraft[field.key]}
                      placeholder={field.placeholder}
                      onChange={(e) => setConfigDraft({ ...configDraft, [field.key]: e.target.value })}
                    />
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleConfigSave}
                disabled={configSaving}
              >
                {configSaving ? "保存中..." : "設定を保存"}
              </button>
            </>
          )}
        </div>
      )}

      {progressError && <p className="error-text">{progressError}</p>}

      {progress && (
        <>
          <div className="admin-topbar">
            <div className="admin-topbar__meta">
              <span className="admin-topbar__month">対象月: {progress.activeMonth}</span>
              <span
                className={`status-pill ${progress.status === "open" ? "status-pill--open" : "status-pill--closed"}`}
              >
                {progress.status === "open" ? "受付中" : "受付終了"}
              </span>
              <span className="admin-topbar__deadline">締切: {progress.deadline}</span>
            </div>

            <div className="kpi-row">
              <KpiTile label="評価対象者数" value={progress.evalueeCount} />
              <KpiTile label="担当者数" value={progress.evaluatorCount} />
              <KpiTile label="評価済み件数" value={progress.doneCount} />
              <KpiTile label="未評価件数" value={progress.remainingCount} />
              <KpiTile
                label="コメント未入力件数"
                value={progress.missingCommentCount}
                warn={progress.missingCommentCount > 0}
              />
              <KpiTile
                label="PDF未紐付け件数"
                value={progress.missingPdfCount}
                warn={progress.missingPdfCount > 0}
              />
            </div>
          </div>

          <div className="admin-main">
            <section className="admin-center">
              <h2 className="section-heading">担当者別進捗</h2>
              {sortedByEvaluator.length === 0 ? (
                <p className="hint-text">データがありません</p>
              ) : (
                <table className="progress-table">
                  <thead>
                    <tr>
                      <th>担当者名</th>
                      <th>評価対象件数</th>
                      <th>完了件数</th>
                      <th>未完了件数</th>
                      <th>完了率</th>
                      <th>最終更新日時</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedByEvaluator.map((row) => (
                      <tr key={row.evaluator} className={row.remaining > 0 ? "" : "progress-row--complete"}>
                        <td>{row.evaluator}</td>
                        <td>{row.assigned}</td>
                        <td>{row.done}</td>
                        <td>{row.remaining}</td>
                        <td>{Math.round(row.rate * 100)}%</td>
                        <td>{row.lastUpdatedAt || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <aside className="admin-right">
              <h2 className="section-heading">Slackリマインド</h2>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={handlePreview}
                disabled={previewLoading}
              >
                {previewLoading ? "取得中..." : "プレビュー"}
              </button>

              {previewError && <p className="error-text">{previewError}</p>}

              {preview && (
                <div className="remind-preview">
                  <pre className="remind-message">{preview.message}</pre>
                  <p className="hint-text">対象: {preview.mentionedEvaluators.join("、") || "なし"}</p>
                  {preview.unresolvedEvaluators.length > 0 && (
                    <p className="error-text">
                      Slack IDが見つからない担当者: {preview.unresolvedEvaluators.join("、")}
                    </p>
                  )}

                  {sendError && <p className="error-text">{sendError}</p>}

                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={handleSend}
                    disabled={sending || preview.mentionedEvaluators.length === 0}
                  >
                    {sending ? "送信中..." : "送信"}
                  </button>
                </div>
              )}

              <hr className="admin-right__divider" />

              <h2 className="section-heading">強制C手動通知</h2>
              <p className="hint-text">
                差し戻し基準による自動C確定以外の、ルール化しづらい強制C確定の対象者向け。
                対象添削者IDと本文を指定してSlackの個別連絡チャンネルへ送信する。
              </p>
              <label className="login-field">
                <span>添削者ID</span>
                <input
                  type="text"
                  value={notifyExaminerId}
                  onChange={(e) => setNotifyExaminerId(e.target.value)}
                  placeholder="N0123456"
                />
              </label>
              <label className="login-field">
                <span>メッセージ本文</span>
                <textarea
                  value={notifyMessage}
                  onChange={(e) => setNotifyMessage(e.target.value)}
                  rows={5}
                  placeholder="送信するメッセージ本文を入力してください"
                />
              </label>
              {notifyError && <p className="error-text">{notifyError}</p>}
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleNotifyExaminee}
                disabled={notifySending}
              >
                {notifySending ? "送信中..." : "送信"}
              </button>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className={`kpi-tile ${warn ? "kpi-tile--warn" : ""}`}>
      <span className="kpi-tile__label">{label}</span>
      <span className="kpi-tile__value">{value}</span>
    </div>
  );
}
